import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, LoaderCircle } from 'lucide-react';
import { BrandAtmosphere } from '@/components/BrandAtmosphere';
import { WebsiteFooter } from '@/components/WebsiteFooter';
import {
  accessEmailHref,
  emptyAccessRequest,
  teamSizes,
  firmWorkTypes,
  specificWorkTypes,
  isAccessRequestConfigured,
  submitAccessRequest,
  validateAccessRequest,
} from '@/lib/access-request';
import type {
  AccessRequestErrors,
  AccessRequestValues,
} from '@/lib/access-request';
import '../sign-up.css';
import '../signup-foundation.css';
import '../request-access.css';

const textFields = [
  {
    name: 'firstname',
    label: 'First name',
    autoComplete: 'given-name',
    maxLength: 100,
  },
  {
    name: 'lastname',
    label: 'Last name',
    autoComplete: 'family-name',
    maxLength: 100,
  },
  {
    name: 'email',
    label: 'Work email',
    autoComplete: 'email',
    type: 'email',
    maxLength: 254,
  },
  {
    name: 'company',
    label: 'Company name',
    autoComplete: 'organization',
    maxLength: 200,
  },
  {
    name: 'phone',
    label: 'Phone number (optional)',
    autoComplete: 'tel',
    type: 'tel',
    maxLength: 40,
  },
] as const;

export function RequestAccessPage() {
  const [values, setValues] = useState<AccessRequestValues>({
    ...emptyAccessRequest,
  });
  const [step, setStep] = useState<1 | 2>(1);
  const [errors, setErrors] = useState<AccessRequestErrors>({});
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle');
  const errorSummary = useRef<HTMLDivElement>(null);
  const successHeading = useRef<HTMLHeadingElement>(null);
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const previousStep = useRef(step);
  const sending = useRef(false);
  const allSelected = specificWorkTypes.every((type) =>
    values.firmWorkType.includes(type.value),
  );

  useEffect(() => {
    const previous = document.title;
    document.title = 'Find out more | Asset Hound';
    return () => {
      document.title = previous;
    };
  }, []);
  useEffect(() => {
    if (validationAttempt || status === 'error') errorSummary.current?.focus();
    if (status === 'success') successHeading.current?.focus();
  }, [validationAttempt, status]);
  useEffect(() => {
    if (previousStep.current !== step) stepHeading.current?.focus();
    previousStep.current = step;
  }, [step]);

  function update(
    name: Exclude<keyof AccessRequestValues, 'firmWorkType'>,
    value: string,
  ) {
    setValues((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: undefined }));
  }
  function updateWork(value: string, checked: boolean) {
    setValues((previous) => {
      const next = new Set(previous.firmWorkType);
      const changed =
        value === 'all' ? specificWorkTypes.map((type) => type.value) : [value];
      changed.forEach((type) => (checked ? next.add(type) : next.delete(type)));
      return { ...previous, firmWorkType: [...next] };
    });
    setErrors((previous) => ({ ...previous, firmWorkType: undefined }));
  }
  function moveBack() {
    setErrors({});
    setStatus('idle');
    setStep(1);
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sending.current) return;
    const nextErrors = validateAccessRequest(
      values,
      step === 1 ? 1 : undefined,
    );
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      if (step === 2 && Object.keys(validateAccessRequest(values, 1)).length)
        setStep(1);
      setValidationAttempt((attempt) => attempt + 1);
      return;
    }
    if (step === 1) {
      setStep(2);
      return;
    }
    sending.current = true;
    setStatus('sending');
    try {
      await submitAccessRequest(values, window.location.href);
      setStatus('success');
      setValues({ ...emptyAccessRequest });
    } catch {
      setStatus('error');
    } finally {
      sending.current = false;
    }
  }

  const choices = [
    ...firmWorkTypes.filter((type) => type.value !== 'other'),
    { value: 'all', label: 'All of the above' },
    firmWorkTypes.find((type) => type.value === 'other')!,
  ];

  return (
    <div className="signup-page access-page">
      <BrandAtmosphere />
      <a href="#main-content" className="signup-skip">
        Skip to content
      </a>
      <header className="signup-container signup-header">
        <Link to="/" className="signup-brand" aria-label="Asset Hound home">
          <img
            src="/brand/asset-hound-icon.png"
            className="signup-brand-mark"
            alt=""
            aria-hidden="true"
            draggable={false}
          />
          <span>asset hound</span>
        </Link>
        <Link to="/" className="access-back">
          <ArrowLeft size={15} aria-hidden="true" /> Back to home
        </Link>
      </header>
      <main
        id="main-content"
        className="signup-container access-main"
        tabIndex={-1}
      >
        <div className="access-intro">
          <h1>Find out more about what we’re building.</h1>
          <p>
            Tell us a little about you and your firm and we’ll be in touch about
            Asset Hound.
          </p>
        </div>
        <div className="access-form-column">
          {status === 'success' ? (
            <section
              className="access-success"
              aria-labelledby="access-success-heading"
            >
              <span className="access-success-mark">
                <Check size={25} aria-hidden="true" />
              </span>
              <h2
                ref={successHeading}
                tabIndex={-1}
                id="access-success-heading"
              >
                Thank you for registering your interest.
              </h2>
              <p>A member of our team will be personally in touch.</p>
              <Link to="/" className="signup-button">
                Back to home <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </section>
          ) : (
            <form
              noValidate
              onSubmit={submit}
              aria-label="Asset Hound enquiry"
              aria-busy={status === 'sending'}
            >
              <ol className="access-steps" aria-label="Enquiry progress">
                {[1, 2].map((number) => (
                  <li
                    key={number}
                    aria-current={step === number ? 'step' : undefined}
                    className={
                      step > number ? 'access-step-complete' : undefined
                    }
                  >
                    <span className="access-step-number">
                      {step > number ? (
                        <Check size={15} aria-label="Complete" />
                      ) : (
                        number
                      )}
                    </span>
                    <span>{number === 1 ? 'Your details' : 'Your firm'}</span>
                  </li>
                ))}
              </ol>
              {!isAccessRequestConfigured() && (
                <p className="access-availability">
                  This form is being prepared. Please{' '}
                  <a href={accessEmailHref}>email us</a>.
                </p>
              )}
              {(Object.values(errors).some(Boolean) || status === 'error') && (
                <div
                  ref={errorSummary}
                  className="access-error-summary"
                  role="alert"
                  tabIndex={-1}
                >
                  <h2>
                    {status === 'error'
                      ? 'Your enquiry hasn’t been confirmed'
                      : 'Check your details'}
                  </h2>
                  {status === 'error' ? (
                    <p>
                      Your details are still here. Please try again, or{' '}
                      <a href={accessEmailHref}>email us</a>.
                    </p>
                  ) : (
                    <ul>
                      {Object.entries(errors)
                        .filter(([, error]) => error)
                        .map(([name, error]) => (
                          <li key={name}>
                            <a href={`#access-${name}`}>{error}</a>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="access-stage">
                <fieldset
                  className="access-step"
                  hidden={step !== 1}
                  disabled={status === 'sending' || step !== 1}
                >
                  <legend className="access-sr-only">Your details</legend>
                  <h2
                    ref={step === 1 ? stepHeading : undefined}
                    tabIndex={-1}
                    className="access-stage-heading"
                  >
                    Your details
                  </h2>
                  <div className="access-fields">
                    {textFields.map((field) => (
                      <div className="access-field" key={field.name}>
                        <label htmlFor={`access-${field.name}`}>
                          {field.label}
                        </label>
                        <input
                          id={`access-${field.name}`}
                          name={field.name}
                          type={'type' in field ? field.type : 'text'}
                          autoComplete={field.autoComplete}
                          maxLength={field.maxLength}
                          required={field.name !== 'phone'}
                          value={values[field.name]}
                          onChange={(event) =>
                            update(field.name, event.target.value)
                          }
                          aria-invalid={!!errors[field.name]}
                          aria-describedby={
                            errors[field.name]
                              ? `error-${field.name}`
                              : undefined
                          }
                        />
                        {errors[field.name] && (
                          <p
                            className="access-field-error"
                            id={`error-${field.name}`}
                          >
                            {errors[field.name]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </fieldset>
                <fieldset
                  className="access-step"
                  hidden={step !== 2}
                  disabled={status === 'sending' || step !== 2}
                >
                  <legend className="access-sr-only">Your firm</legend>
                  <h2
                    ref={step === 2 ? stepHeading : undefined}
                    tabIndex={-1}
                    className="access-stage-heading"
                  >
                    Tell us a bit about your firm.
                  </h2>
                  <fieldset
                    className="access-work-types"
                    aria-describedby="firm-work-type-hint"
                  >
                    <legend>
                      Select the type of Insolvency &amp; Restructuring work you
                      do:
                    </legend>
                    <p className="access-hint" id="firm-work-type-hint">
                      You can select more than one.
                    </p>
                    <div
                      className="access-work-choices"
                      aria-invalid={!!errors.firmWorkType}
                    >
                      {choices.map((type, index) => (
                        <label key={type.value}>
                          <input
                            id={
                              index === 0
                                ? 'access-firmWorkType'
                                : `access-firmWorkType-${type.value}`
                            }
                            type="checkbox"
                            name="firmWorkType"
                            value={type.value}
                            checked={
                              type.value === 'all'
                                ? allSelected
                                : values.firmWorkType.includes(type.value)
                            }
                            onChange={(event) =>
                              updateWork(type.value, event.target.checked)
                            }
                            aria-invalid={!!errors.firmWorkType}
                            aria-describedby={
                              errors.firmWorkType
                                ? 'firm-work-type-hint error-firmWorkType'
                                : 'firm-work-type-hint'
                            }
                          />
                          <span>{type.label}</span>
                        </label>
                      ))}
                    </div>
                    {errors.firmWorkType && (
                      <p className="access-field-error" id="error-firmWorkType">
                        {errors.firmWorkType}
                      </p>
                    )}
                  </fieldset>
                  <div className="access-field access-team-size">
                    <label htmlFor="access-teamSize">Team size</label>
                    <select
                      id="access-teamSize"
                      name="teamSize"
                      required
                      value={values.teamSize}
                      onChange={(event) =>
                        update('teamSize', event.target.value)
                      }
                      aria-invalid={!!errors.teamSize}
                      aria-describedby={
                        errors.teamSize
                          ? 'team-size-hint error-teamSize'
                          : 'team-size-hint'
                      }
                    >
                      <option value="">Select team size</option>
                      {teamSizes.map((size) => (
                        <option key={size.value} value={size.value}>
                          {size.label}
                        </option>
                      ))}
                    </select>
                    <p className="access-hint" id="team-size-hint">
                      Total people within your team.
                    </p>
                    {errors.teamSize && (
                      <p className="access-field-error" id="error-teamSize">
                        {errors.teamSize}
                      </p>
                    )}
                  </div>
                </fieldset>
              </div>
              <div className="access-trap" aria-hidden="true">
                <label htmlFor="access-websiteTrap">
                  Leave this field empty
                </label>
                <input
                  id="access-websiteTrap"
                  name="websiteTrap"
                  tabIndex={-1}
                  autoComplete="off"
                  value={values.websiteTrap}
                  onChange={(event) =>
                    update('websiteTrap', event.target.value)
                  }
                />
              </div>
              <div className="access-submit-row">
                <p>
                  We’ll use your details to respond to your enquiry. Read our{' '}
                  <a href="/privacy">Privacy Policy</a>.
                </p>
                <div className="access-actions">
                  {step === 2 && (
                    <button
                      type="button"
                      className="access-back-button"
                      onClick={moveBack}
                      disabled={status === 'sending'}
                    >
                      <ArrowLeft size={16} aria-hidden="true" /> Back
                    </button>
                  )}
                  <button
                    className="signup-button access-submit"
                    type="submit"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? (
                      <>
                        Sending{' '}
                        <LoaderCircle
                          className="access-spinner"
                          size={18}
                          aria-hidden="true"
                        />
                      </>
                    ) : (
                      <>
                        {step === 1 ? 'Next' : 'Send'}{' '}
                        <ArrowRight size={18} aria-hidden="true" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </main>
      <WebsiteFooter />
    </div>
  );
}
